import React, { useEffect, useState, useCallback } from 'react';
import Loading from '../Loading';
import { ReceptionStatus, ReceptionType, FlowType } from 'enums/reception.enum';
import { ReceptionDTO } from 'models/reception';
import { ReceptionItemDTO } from 'models/reception-item';
import moment from 'moment';

const TYPE_LABELS: Record<ReceptionType, string> = {
    'GENERAL': '일반',
    'EXCHANGE': '맞교환',
    'TRIP': '출장',
    'COMP_SALE': '보상판매',
    'VISIT': '방문',
    'SALE': '판매',
    'REPAIR_COMP': '리퍼보상',
    'NEW_EXCHANGE': '신상맞교환',
    // 'COMP_SALE_TRIP': '보상판매출장',
};

interface DeliveryCompanyOption {
    name: string;
    isCustom: boolean;
}

type Props = {
    show: boolean;
    type?: 'inbound' | 'outbound';
    loading?: boolean;
    initial?: ReceptionDTO | null;
    onSave: (reception: ReceptionDTO, close: boolean) => Promise<void> | void;
    onClose: () => void;
};

const defaultReception: ReceptionDTO = {
    status: ReceptionStatus.RECEIVED,
    type: ReceptionType.GENERAL,
    estimateAmount: 0,
    repairCost: 0,
    tripFeeAmount: 0,
    tripFee: 0,
    logisticsFee: 0,
    profit: 0,
    items: []
};

const DlvModal: React.FC<Props> = ({
    show,
    type = 'inbound',
    loading = false,
    initial,
    onSave,
    onClose,
}) => {

    const [form, setForm] = useState<ReceptionDTO>(defaultReception);
    const [deliveryCompanyOptions, setDeliveryCompanyOptions] = useState<DeliveryCompanyOption[]>([]);
    const [selectedDlvCompany, setSelectedDlvCompany] = useState<string>('로젠');
    const [customDlvCompany, setCustomDlvCompany] = useState<string>('');
    const [useCustomDlvCompany, setUseCustomDlvCompany] = useState<boolean>(false);
    const [selectedDlvFee, setSelectedDlvFee] = useState<number>(5000);
    const [itemsDescription, setItemsDescription] = useState<string>('');
    const [selectedDate, setSelectedDate] = useState<string>(moment().format('YYYY-MM-DD'));

    const selectedFlowType = type === 'inbound' ? FlowType.INBOUND : FlowType.OUTBOUND_COURIER;

    const generateItemsDescription = useCallback((items: ReceptionItemDTO[]): string => {
        if (!items || items.length === 0) return '';
        const descriptions: string[] = [];
        items.forEach(item => {
            if (item.modelName) descriptions.push(item.modelName);
            if (item.options && item.options.length > 0) {
                item.options.forEach(option => {
                    const optionText = option.quantity > 1
                        ? `${option.productName}×${option.quantity}`
                        : option.productName;
                    descriptions.push(optionText);
                });
            }
            if (item.summary && !item.options?.length && !item.modelName) {
                descriptions.push(item.summary);
            }
        });
        return descriptions.join(', ');
    }, []);

    const loadDeliveryCompanyOptions = () => {
        setDeliveryCompanyOptions([
            { name: '로젠', isCustom: false },
            { name: '경동', isCustom: false },
            { name: '대신', isCustom: false },
            { name: '직접입력', isCustom: true }
        ]);
    };

    useEffect(() => {
        if (show) {
            if (initial) {
                setForm(initial);
                if (type === 'inbound') {
                    const existingDate = initial.items?.find(item => item.courierInboundDate)?.courierInboundDate;
                    setSelectedDate(existingDate || moment().format('YYYY-MM-DD'));
                } else {
                    const existingDate = initial.items?.find(item => item.courierOutboundDate)?.courierOutboundDate;
                    setSelectedDate(existingDate || moment().format('YYYY-MM-DD'));
                }

                if (type === 'inbound' && initial.inboundNote) {
                    setItemsDescription(initial.inboundNote);
                } else if (type === 'outbound' && initial.outboundNote) {
                    setItemsDescription(initial.outboundNote);
                } else {
                    setItemsDescription(generateItemsDescription(initial.items || []));
                }

                const firstItemWithDlvCompany = initial.items?.find(item => item.dlvCompany);
                if (firstItemWithDlvCompany?.dlvCompany) {
                    const predefinedCompanies = ['로젠', '경동', '대신'];
                    if (predefinedCompanies.includes(firstItemWithDlvCompany.dlvCompany)) {
                        setSelectedDlvCompany(firstItemWithDlvCompany.dlvCompany);
                        setUseCustomDlvCompany(false);
                        setCustomDlvCompany('');
                    } else {
                        setSelectedDlvCompany('직접입력');
                        setUseCustomDlvCompany(true);
                        setCustomDlvCompany(firstItemWithDlvCompany.dlvCompany);
                    }
                } else {
                    setSelectedDlvCompany('로젠');
                    setUseCustomDlvCompany(false);
                    setCustomDlvCompany('');
                }

                const firstItemWithDlvFee = initial.items?.find(item => item.dlvFee !== null && item.dlvFee !== undefined);
                if (firstItemWithDlvFee?.dlvFee !== undefined) {
                    setSelectedDlvFee(firstItemWithDlvFee.dlvFee);
                } else {
                    setSelectedDlvFee(5000);
                }
            } else {
                setForm(defaultReception);
                setItemsDescription('');
                setSelectedDlvCompany('로젠');
                setUseCustomDlvCompany(false);
                setCustomDlvCompany('');
                setSelectedDlvFee(5000);
            }
            loadDeliveryCompanyOptions();
        }
    }, [initial, show, type, generateItemsDescription]);

    const onChange = useCallback((name: keyof ReceptionDTO, value: any) => {
        setForm(prev => ({ ...prev, [name]: value }));
    }, []);

    const onDlvCompanyChange = useCallback((value: string) => {
        if (value === '직접입력') {
            setUseCustomDlvCompany(true);
            setSelectedDlvCompany(value);
            setCustomDlvCompany('');
        } else {
            setUseCustomDlvCompany(false);
            setSelectedDlvCompany(value);
            setCustomDlvCompany('');
        }
    }, []);

    const onCustomDlvCompanyChange = useCallback((value: string) => {
        setCustomDlvCompany(value);
    }, []);

    const onDlvFeeChange = useCallback((value: number) => {
        setSelectedDlvFee(value);
    }, []);

    const onItemsDescriptionChange = useCallback((value: string) => {
        setItemsDescription(value);
    }, []);

    const handleClose = useCallback(() => {
        onClose();
    }, [onClose]);

    const handleSave = useCallback(async () => {
        if (!form.phone1?.trim()) {
            alert('연락처를 입력해주세요.');
            return;
        }

        const updatedItems = form.items.map(item => {
            if (type === 'inbound') {
                return {
                    ...item,
                    courierInboundDate: selectedDate,
                    flow: FlowType.INBOUND
                };
            } else {
                const finalDlvCompany = useCustomDlvCompany ? customDlvCompany : selectedDlvCompany;
                return {
                    ...item,
                    courierOutboundDate: selectedDate,
                    flow: FlowType.OUTBOUND_COURIER,
                    dlvCompany: finalDlvCompany,
                    dlvFee: selectedDlvFee
                };
            }
        });

        const finalForm: ReceptionDTO = {
            ...form,
            items: updatedItems,
            ...(type === 'inbound'
                ? {
                    inboundNote: itemsDescription,
                    status: ReceptionStatus.REPAIRING
                }
                : { outboundNote: itemsDescription }
            )
        };

        await onSave(finalForm, true);
    }, [form, type, selectedDate, useCustomDlvCompany, selectedDlvCompany, customDlvCompany, selectedDlvFee, itemsDescription, onSave]);

    /**
     * 신규 추가: 출고 일자 및 정보 삭제(초기화) 핸들러
     */
    const handleDeleteDate = useCallback(async () => {
        if (!confirm('출고 정보를 삭제하시겠습니까?')) return;

        const updatedItems = form.items.map(item => {
            if (type === 'outbound') {
                return {
                    ...item,
                    courierOutboundDate: null,
                    flow: null,
                    dlvCompany: null,
                    dlvFee: null
                };
            }
            return item;
        });

        const finalForm: ReceptionDTO = {
            ...form,
            items: updatedItems,
            ...(type === 'outbound' && { outboundNote: '' })
        };

        await onSave(finalForm, true);
    }, [form, type, onSave]);

    if (!show) return null;

    const title = type === 'inbound' ? '택배입고모달' : '택배출고모달';

    return (
        <>
            <div
                className={`modal fade ${show ? 'show' : ''}`}
                style={{
                    display: show ? 'block' : 'none',
                    backgroundColor: 'rgba(0,0,0,0.3)',
                    zIndex: 1070,
                    pointerEvents: 'none'
                }}
                tabIndex={-1}
                role="dialog"
            >
                <div
                    className="modal-dialog"
                    role="document"
                    style={{
                        maxWidth: window.innerWidth < 768 ? '90%' : '800px',
                        margin: '2rem auto',
                        pointerEvents: 'auto'
                    }}
                >
                    <div className="modal-content">
                        <div className="modal-header bg-primary">
                            <h5 className="modal-title text-white">
                                <i className={`fas ${type === 'inbound' ? 'fa-arrow-down' : 'fa-arrow-up'} mr-2`}></i>
                                {title}
                            </h5>
                            <button type="button" className="close text-white" onClick={handleClose}>
                                <span>&times;</span>
                            </button>
                        </div>

                        <div className="modal-body p-3" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                            {loading && <Loading isWhole={false} />}

                            <div className="card mb-2">
                                <div className="card-header bg-info text-white py-2">
                                    <h6 className="mb-0">기본 정보</h6>
                                </div>
                                <div className="card-body p-2">
                                    <div className="row mb-2">
                                        <div className="col-6">
                                            <label className="form-label mb-1 small">
                                                {type === 'inbound' ? '택배입고일' : '택배출고일'}
                                            </label>
                                            <input
                                                type="date"
                                                className="form-control form-control-sm"
                                                value={selectedDate}
                                                onChange={(e) => setSelectedDate(e.target.value)}
                                                style={{ backgroundColor: '#fff3cd', fontWeight: 'bold' }}
                                            />
                                            {/* <input
                                                type="date"
                                                className="form-control form-control-sm"
                                                value={moment().format('YYYY-MM-DD')}
                                                disabled
                                                style={{ backgroundColor: '#fff3cd', fontWeight: 'bold' }}
                                            /> */}
                                        </div>

                                        <div className="col-6">
                                            <label className="form-label mb-1 small">처리유형</label>
                                            <input
                                                type="text"
                                                className="form-control form-control-sm"
                                                value={TYPE_LABELS[form.type] || ''}
                                                disabled
                                                style={{
                                                    color: 'red',
                                                    fontWeight: 'bold',
                                                    backgroundColor: '#f8f9fa',
                                                    border: '2px solid red'
                                                }}
                                            />
                                        </div>
                                    </div>

                                    <div className="row mb-2">
                                        <div className="col-6">
                                            <label className="form-label mb-1 small">고객명</label>
                                            <input
                                                className="form-control form-control-sm"
                                                value={form.customerName || ''}
                                                onChange={(e) => onChange('customerName', e.target.value)}
                                                placeholder="고객명"
                                            />
                                        </div>

                                        <div className="col-6">
                                            <label className="form-label mb-1 small">연락처1 <span className="text-danger">*</span></label>
                                            <input
                                                className="form-control form-control-sm"
                                                value={form.phone1 || ''}
                                                onChange={(e) => onChange('phone1', e.target.value)}
                                                placeholder="010-0000-0000"
                                            />
                                        </div>
                                    </div>

                                    <div className="mb-2">
                                        <label className="form-label mb-1 small">상세주소</label>
                                        <input
                                            className="form-control form-control-sm"
                                            value={`${form.province || ''} ${form.city || ''} ${form.address || ''}`.trim()}
                                            disabled
                                            style={{ backgroundColor: '#f8f9fa' }}
                                        />
                                    </div>

                                    {type === "inbound" && (
                                        <div className="row mb-2">
                                            <div className="col-6">
                                                <label className="form-label mb-1 small">접수내용</label>
                                                <textarea
                                                    className="form-control form-control-sm"
                                                    rows={2}
                                                    value={form.requestNote || ''}
                                                    onChange={(e) => onChange('requestNote', e.target.value)}
                                                    placeholder="접수내용"
                                                />
                                            </div>
                                            <div className="col-6">
                                                <label className="form-label mb-1 small">처리내용</label>
                                                <textarea
                                                    className="form-control form-control-sm"
                                                    rows={2}
                                                    value={form.handleNote || ''}
                                                    onChange={(e) => onChange('handleNote', e.target.value)}
                                                    placeholder="처리내용"
                                                />
                                            </div>
                                        </div>
                                    )}

                                    <div className="row mb-2">
                                        <div className="col-6">
                                            <label className="form-label mb-1 small font-weight-bold">
                                                입고/택발
                                            </label>
                                            <input
                                                type="text"
                                                className="form-control form-control-sm"
                                                value={selectedFlowType === FlowType.INBOUND ? '입고' : '택발'}
                                                disabled
                                                style={{
                                                    backgroundColor: '#f8f9fa',
                                                    color: 'red',
                                                    fontWeight: 'bold',
                                                    border: '2px solid #6c757d'
                                                }}
                                            />
                                        </div>

                                        {type === "outbound" && (
                                            <div className="col-6">
                                                <label className="form-label mb-1 small font-weight-bold">
                                                    선불/착불
                                                </label>
                                                <select
                                                    className="form-control form-control-sm"
                                                    value={selectedDlvFee}
                                                    onChange={(e) => onDlvFeeChange(Number(e.target.value))}
                                                    style={{ border: '2px solid #007bff' }}
                                                >
                                                    <option value={5000}>선불</option>
                                                    <option value={0}>착불</option>
                                                </select>
                                            </div>
                                        )}
                                    </div>

                                    {type === "outbound" && (
                                        <div className="row mb-2">
                                            <div className="col-6">
                                                <label className="form-label mb-1 small font-weight-bold">
                                                    운송사 <span className="text-danger">*</span>
                                                </label>
                                                <select
                                                    className="form-control form-control-sm"
                                                    value={selectedDlvCompany}
                                                    onChange={(e) => onDlvCompanyChange(e.target.value)}
                                                    style={{ border: '2px solid #007bff' }}
                                                >
                                                    {deliveryCompanyOptions.map(option => (
                                                        <option key={option.name} value={option.name}>
                                                            {option.name}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>

                                            {useCustomDlvCompany && (
                                                <div className="col-6">
                                                    <label className="form-label mb-1 small font-weight-bold">
                                                        운송사 직접입력
                                                    </label>
                                                    <input
                                                        type="text"
                                                        className="form-control form-control-sm"
                                                        value={customDlvCompany}
                                                        onChange={(e) => onCustomDlvCompanyChange(e.target.value)}
                                                        placeholder="운송사명 입력"
                                                        style={{ border: '2px solid #007bff' }}
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    <div className="row">
                                        <div className="col-12">
                                            <label className="form-label mb-1 small font-weight-bold">
                                                {type === 'inbound' ? '택배입고물품' : '택배출고물품'}
                                                <span className="text-muted ml-2" style={{ fontSize: '0.75rem' }}>
                                                    (수정 가능)
                                                </span>
                                            </label>
                                            <textarea
                                                className="form-control form-control-sm"
                                                rows={3}
                                                value={itemsDescription}
                                                onChange={(e) => onItemsDescriptionChange(e.target.value)}
                                                placeholder="택배물품 내역을 입력하세요"
                                                style={{
                                                    fontWeight: 'bold',
                                                    border: '2px solid #28a745'
                                                }}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="modal-footer p-2 d-flex justify-content-between">
                            {/* 좌측: 삭제 버튼 (출고 타입일 때만 노출) */}
                            <div>
                                {type === 'outbound' && (
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-danger"
                                        onClick={handleDeleteDate}
                                        disabled={loading}
                                    >
                                        <i className="fas fa-trash-alt mr-1"></i>
                                        출고취소
                                    </button>
                                )}
                            </div>

                            {/* 우측: 닫기 및 저장 버튼 */}
                            <div>
                                <button
                                    type="button"
                                    className="btn btn-sm btn-secondary mr-1"
                                    onClick={handleClose}
                                    disabled={loading}
                                >
                                    <i className="fas fa-times mr-1"></i>
                                    닫기
                                </button>
                                <button
                                    type="button"
                                    className={`btn btn-sm ${type === 'inbound' ? 'btn-success' : 'btn-warning'}`}
                                    onClick={handleSave}
                                    disabled={loading}
                                >
                                    {loading ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm mr-1"></span>
                                            처리중...
                                        </>
                                    ) : (
                                        <>
                                            <i className="fas fa-save mr-1"></i>
                                            저장
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default DlvModal;